import { DateTime } from 'luxon';

import { ListTemplate, Organization } from '../../types';
import { parseDate } from '../../utils/DateTime';
import { OrganizationComponent, OrganizationTemplateType } from '../components/organization.template';
import { DocumentTypePopulator } from '../DocumentGenerator';
import { applyListMeta, formatCurrency } from './formatters';

export type ProfitAndLossReportInputType = {
  organization: Organization;
  profitAndLossAccounts: Array<{
    id: number;
    accountName: string;
    accountNumber: string;
    accountType: string;
    fragmentAccountPath: string;
    profitLoss: number;
    previousPeriodProfitLoss?: number;
    previousYearProfitLoss?: number;
  }>;
  reportPeriod: { start: string; end: string };
  compareTo: { previousPeriod?: boolean; previousYear?: boolean };
};

export type FlattenedPLAccount = {
  id: string;
  accountNumber: string;
  accountName: string;
  accountType: string;
  balance: string;
  balanceRaw: string;
  balanceNum: number;
  balanceFmt: string;
  previousPeriodBalance?: string;
  previousYearBalance?: string;
  depth: number;
  hasChildren: boolean;
  isLastChild: boolean;
  isSubtotal: boolean;
  isZeroBalance: boolean;
  parentGroup?: string;
};

export type ProfitAndLossReportOutputType = {
  organization: OrganizationTemplateType;
  accrualBasis: string;
  reportDate: string;
  reportPeriod: string;
  income: {
    accounts: ListTemplate<FlattenedPLAccount>;
    balance: string;
    balanceRaw: string;
    balanceFmt: string;
    previousPeriodBalance?: string;
    previousYearBalance?: string;
  };
  costOfGoodsSold: {
    accounts: ListTemplate<FlattenedPLAccount>;
    balance: string;
    balanceRaw: string;
    balanceFmt: string;
    previousPeriodBalance?: string;
    previousYearBalance?: string;
  };
  expenses: {
    accounts: ListTemplate<FlattenedPLAccount>;
    balance: string;
    balanceRaw: string;
    balanceFmt: string;
    previousPeriodBalance?: string;
    previousYearBalance?: string;
  };
  otherIncome: {
    accounts: ListTemplate<FlattenedPLAccount>;
    balance: string;
    balanceRaw: string;
    balanceFmt: string;
    previousPeriodBalance?: string;
    previousYearBalance?: string;
  };
  otherExpenses: {
    accounts: ListTemplate<FlattenedPLAccount>;
    balance: string;
    balanceRaw: string;
    balanceFmt: string;
    previousPeriodBalance?: string;
    previousYearBalance?: string;
  };
  grossProfit: {
    balance: string;
    balanceRaw: string;
    balanceFmt: string;
    previousPeriodBalance?: string;
    previousYearBalance?: string;
  };
  netOperatingIncome: {
    balance: string;
    balanceRaw: string;
    balanceFmt: string;
    previousPeriodBalance?: string;
    previousYearBalance?: string;
  };
  netOtherIncome: {
    balance: string;
    balanceRaw: string;
    balanceFmt: string;
    previousPeriodBalance?: string;
    previousYearBalance?: string;
  };
  netIncome: {
    balance: string;
    balanceRaw: string;
    balanceFmt: string;
    previousPeriodBalance?: string;
    previousYearBalance?: string;
  };
};

function filterAccountsWithChildren(accounts: FlattenedPLAccount[]): FlattenedPLAccount[] {
  const shouldKeepAccount = (account: FlattenedPLAccount): boolean => {
    // Keep if account has non-zero balance
    if (!account.isZeroBalance) return true;

    // Keep if any descendant has non-zero balance
    return accounts.some((otherAccount) => otherAccount.id.startsWith(account.id + '/') && !otherAccount.isZeroBalance);
  };

  return accounts.filter((account) => shouldKeepAccount(account));
}

function buildHierarchy(accounts: any[], showAccountNumbers?: boolean): FlattenedPLAccount[] {
  // Sort accounts by account number within each parent group (create copy to avoid read-only error)
  const sortedAccounts = [...accounts].sort((a, b) => {
    const pathA = a.fragmentAccountPath.split('/');
    const pathB = b.fragmentAccountPath.split('/');

    // Compare each level of the path
    const maxDepth = Math.max(pathA.length, pathB.length);

    for (let i = 0; i < maxDepth; i++) {
      const segmentA = pathA[i];
      const segmentB = pathB[i];

      // If one path is shorter, it comes first (parent before child)
      if (!segmentA) return -1;
      if (!segmentB) return 1;

      // If segments are different, sort by account number at this level
      if (segmentA !== segmentB) {
        // Find the account numbers for these segments
        const accountA = accounts.find(
          (acc) =>
            acc.fragmentAccountPath
              .split('/')
              .slice(0, i + 1)
              .join('/') === pathA.slice(0, i + 1).join('/')
        );
        const accountB = accounts.find(
          (acc) =>
            acc.fragmentAccountPath
              .split('/')
              .slice(0, i + 1)
              .join('/') === pathB.slice(0, i + 1).join('/')
        );

        const numberA = parseInt(accountA?.accountNumber);
        const numberB = parseInt(accountB?.accountNumber);

        if (numberA !== numberB) {
          return numberA - numberB;
        }

        // Fallback to segment name comparison
        return segmentA.localeCompare(segmentB);
      }
    }

    return 0;
  });

  const flattenedAccounts: FlattenedPLAccount[] = [];
  const processedPaths = new Set<string>();

  // Build account hierarchy
  sortedAccounts.forEach((account) => {
    const pathSegments = account.fragmentAccountPath.split('/');

    // Add parent accounts if they don't exist
    for (let i = 1; i <= pathSegments.length; i++) {
      const partialPath = pathSegments.slice(0, i).join('/');

      if (!processedPaths.has(partialPath)) {
        const isCurrentAccount = partialPath === account.fragmentAccountPath;
        const parentPath = pathSegments.slice(0, i - 1).join('/');

        if (isCurrentAccount) {
          // This is the actual account
          const balanceValue = Math.round(account.profitLoss || 0) / 100; // Convert cents to dollars with proper rounding
          const isZero = balanceValue === 0;

          // Include account number in name if showAccountNumbers is true
          const displayName =
            showAccountNumbers && account.accountNumber
              ? `${account.accountNumber} - ${account.accountName || pathSegments[i - 1]}`
              : account.accountName || pathSegments[i - 1];

          flattenedAccounts.push({
            id: account.fragmentAccountPath,
            accountNumber: account.accountNumber || '',
            accountName: displayName,
            accountType: account.accountType,
            balance: balanceValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
            balanceRaw: balanceValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), // Format with commas and proper precision
            balanceNum: balanceValue, // Raw numeric value for calculations
            balanceFmt: formatCurrency(balanceValue),
            previousPeriodBalance: account.previousPeriodProfitLoss
              ? (Math.round(account.previousPeriodProfitLoss) / 100).toFixed(2)
              : undefined,
            previousYearBalance: account.previousYearProfitLoss
              ? (Math.round(account.previousYearProfitLoss) / 100).toFixed(2)
              : undefined,
            depth: i - 1,
            hasChildren: false,
            isLastChild: false,
            isSubtotal: false,
            isZeroBalance: isZero,
            parentGroup: parentPath || undefined,
          });
        } else {
          // This is a parent account (group) - need to find its proper accountType
          const parentAccount = sortedAccounts.find((acc) => acc.fragmentAccountPath === partialPath);
          const accountType = parentAccount ? parentAccount.accountType : account.accountType;

          flattenedAccounts.push({
            id: partialPath,
            accountNumber: '',
            accountName: pathSegments[i - 1],
            accountType: accountType,
            balance: '0.00',
            balanceRaw: '0.00',
            balanceNum: 0,
            balanceFmt: '',
            depth: i - 1,
            hasChildren: true,
            isLastChild: false,
            isSubtotal: false,
            isZeroBalance: true,
            parentGroup: parentPath || undefined,
          });
        }

        processedPaths.add(partialPath);
      }
    }
  });

  // Calculate totals for parent accounts and add total rows
  const pathTotals = new Map<
    string,
    { balance: number; previousPeriod?: number; previousYear?: number; childCount: number }
  >();

  // First pass: calculate totals
  flattenedAccounts.forEach((account) => {
    if (!account.hasChildren) {
      const pathSegments = account.id.split('/');
      const previousPeriod = account.previousPeriodBalance ? parseFloat(account.previousPeriodBalance) : undefined;
      const previousYear = account.previousYearBalance ? parseFloat(account.previousYearBalance) : undefined;

      // Update all parent paths
      for (let i = 1; i < pathSegments.length; i++) {
        const parentPath = pathSegments.slice(0, i).join('/');
        const current = pathTotals.get(parentPath) || { balance: 0, previousPeriod: 0, previousYear: 0, childCount: 0 };

        current.balance += account.balanceNum;
        if (previousPeriod !== undefined) {
          current.previousPeriod = (current.previousPeriod || 0) + previousPeriod;
        }
        if (previousYear !== undefined) {
          current.previousYear = (current.previousYear || 0) + previousYear;
        }
        current.childCount++;

        pathTotals.set(parentPath, current);
      }
    }
  });

  // Second pass: update parent accounts with totals and add total rows
  const result: FlattenedPLAccount[] = [];
  const addedTotalRows = new Set<string>();

  flattenedAccounts.forEach((account, index) => {
    // Update parent account totals
    if (account.hasChildren) {
      const totals = pathTotals.get(account.id);
      if (totals && totals.childCount > 0) {
        account.balance = totals.balance.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
        account.balanceRaw = totals.balance.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
        account.balanceNum = totals.balance;
        account.balanceFmt = ''; // Parent accounts don't show currency formatting
        account.previousPeriodBalance = totals.previousPeriod
          ? totals.previousPeriod.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          : undefined;
        account.previousYearBalance = totals.previousYear
          ? totals.previousYear.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          : undefined;
        account.isZeroBalance = totals.balance === 0;
      }
    }

    result.push(account);

    // Check if this is the last child of a parent that has children
    const nextAccount = flattenedAccounts[index + 1];
    const currentPathSegments = account.id.split('/');
    const nextPathSegments = nextAccount?.id.split('/') || [];

    // Find if we're at the end of a group
    for (let currentDepth = account.depth; currentDepth >= 0; currentDepth--) {
      const currentParentPath = currentPathSegments.slice(0, currentDepth + 1).join('/');
      const nextParentPath = nextPathSegments.slice(0, currentDepth + 1).join('/');

      if (currentParentPath !== nextParentPath) {
        const parentTotals = pathTotals.get(currentParentPath);
        if (parentTotals && parentTotals.childCount > 1 && !addedTotalRows.has(currentParentPath)) {
          const parentAccount = flattenedAccounts.find((acc) => acc.id === currentParentPath);
          if (parentAccount) {
            // Include parent's own balance in the total
            const totalWithParent = parentTotals.balance + parentAccount.balanceNum;

            result.push({
              id: `total-${currentParentPath}`,
              accountNumber: '',
              accountName: `Total ${parentAccount.accountName}`,
              accountType: parentAccount.accountType,
              balance: totalWithParent.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
              balanceRaw: totalWithParent.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
              balanceNum: totalWithParent,
              balanceFmt: formatCurrency(totalWithParent),
              previousPeriodBalance: parentTotals.previousPeriod
                ? parentTotals.previousPeriod.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                : undefined,
              previousYearBalance: parentTotals.previousYear
                ? parentTotals.previousYear.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                : undefined,
              depth: currentDepth,
              hasChildren: false,
              isLastChild: true,
              isSubtotal: true,
              isZeroBalance: totalWithParent === 0,
              parentGroup: currentParentPath,
            });
            addedTotalRows.add(currentParentPath);
          }
        }
      }
    }
  });

  return result;
}

export const profitAndLossReportTemplate: DocumentTypePopulator<
  ProfitAndLossReportInputType,
  void,
  ProfitAndLossReportOutputType
> = {
  populate: (input, _, globalOptions) => {
    const { organization, profitAndLossAccounts, reportPeriod, compareTo } = input;

    // Merge settings from extendedData if available
    const settings = globalOptions?._settings || {};
    const showAccountNumbers = settings.showAccountNumbers ?? globalOptions?.showAccountNumbers ?? false;
    const hideZeroBalance = settings.hideZeroBalance ?? globalOptions?.hideZeroBalance ?? false;

    // Group accounts by type first, then build hierarchies for each group
    const accountGroups = {
      income: profitAndLossAccounts.filter((account) => account.accountType === 'Income'),
      directCosts: profitAndLossAccounts.filter((account) => account.accountType === 'DirectCosts'),
      indirectCosts: profitAndLossAccounts.filter((account) => account.accountType === 'IndirectCosts'),
      otherIncome: profitAndLossAccounts.filter((account) => account.accountType === 'OtherIncome'),
      otherCosts: profitAndLossAccounts.filter((account) => account.accountType === 'OtherCosts'),
    };

    // Build flattened hierarchies for each group
    const flattenedGroups = {
      income: buildHierarchy(accountGroups.income, showAccountNumbers),
      costOfGoodsSold: buildHierarchy(accountGroups.directCosts, showAccountNumbers),
      expenses: buildHierarchy(accountGroups.indirectCosts, showAccountNumbers),
      otherIncome: buildHierarchy(accountGroups.otherIncome, showAccountNumbers),
      otherExpenses: buildHierarchy(accountGroups.otherCosts, showAccountNumbers),
    };

    // Filter zero balances if requested
    if (hideZeroBalance) {
      (Object.keys(flattenedGroups) as Array<keyof typeof flattenedGroups>).forEach((key) => {
        flattenedGroups[key] = filterAccountsWithChildren(flattenedGroups[key]);
      });
    }

    // Calculate totals for each group
    const calculateGroupTotals = (accounts: FlattenedPLAccount[]) => {
      const balance = accounts
        .filter((acc) => !acc.hasChildren && !acc.isSubtotal)
        .reduce((sum, acc) => sum + acc.balanceNum, 0);

      const previousPeriodBalance = accounts
        .filter((acc) => !acc.hasChildren && !acc.isSubtotal && acc.previousPeriodBalance)
        .reduce((sum, acc) => sum + parseFloat(acc.previousPeriodBalance || '0'), 0);

      const previousYearBalance = accounts
        .filter((acc) => !acc.hasChildren && !acc.isSubtotal && acc.previousYearBalance)
        .reduce((sum, acc) => sum + parseFloat(acc.previousYearBalance || '0'), 0);

      return {
        accounts: applyListMeta(accounts),
        balance: balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        balanceRaw: balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        balanceNum: balance, // Add raw numeric value for calculations
        balanceFmt: formatCurrency(balance),
        previousPeriodBalance: compareTo?.previousPeriod
          ? previousPeriodBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          : undefined,
        previousYearBalance: compareTo?.previousYear
          ? previousYearBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          : undefined,
        previousPeriodNum: previousPeriodBalance, // Add raw numeric values
        previousYearNum: previousYearBalance,
      };
    };

    const income = calculateGroupTotals(flattenedGroups.income);
    const costOfGoodsSold = calculateGroupTotals(flattenedGroups.costOfGoodsSold);
    const expenses = calculateGroupTotals(flattenedGroups.expenses);
    const otherIncome = calculateGroupTotals(flattenedGroups.otherIncome);
    const otherExpenses = calculateGroupTotals(flattenedGroups.otherExpenses);

    // Calculate derived totals using raw numeric values
    const grossProfitValue = income.balanceNum - costOfGoodsSold.balanceNum;
    const grossProfitPreviousPeriod = compareTo?.previousPeriod
      ? (income.previousPeriodNum || 0) - (costOfGoodsSold.previousPeriodNum || 0)
      : undefined;
    const grossProfitPreviousYear = compareTo?.previousYear
      ? (income.previousYearNum || 0) - (costOfGoodsSold.previousYearNum || 0)
      : undefined;

    const netOperatingIncomeValue = grossProfitValue - expenses.balanceNum;
    const netOperatingIncomePreviousPeriod =
      grossProfitPreviousPeriod !== undefined
        ? grossProfitPreviousPeriod - (expenses.previousPeriodNum || 0)
        : undefined;
    const netOperatingIncomePreviousYear =
      grossProfitPreviousYear !== undefined ? grossProfitPreviousYear - (expenses.previousYearNum || 0) : undefined;

    const netOtherIncomeValue = otherIncome.balanceNum - otherExpenses.balanceNum;
    const netOtherIncomePreviousPeriod = compareTo?.previousPeriod
      ? (otherIncome.previousPeriodNum || 0) - (otherExpenses.previousPeriodNum || 0)
      : undefined;
    const netOtherIncomePreviousYear = compareTo?.previousYear
      ? (otherIncome.previousYearNum || 0) - (otherExpenses.previousYearNum || 0)
      : undefined;

    const netIncomeValue = netOperatingIncomeValue + netOtherIncomeValue;
    const netIncomePreviousPeriod =
      netOperatingIncomePreviousPeriod !== undefined && netOtherIncomePreviousPeriod !== undefined
        ? netOperatingIncomePreviousPeriod + netOtherIncomePreviousPeriod
        : undefined;
    const netIncomePreviousYear =
      netOperatingIncomePreviousYear !== undefined && netOtherIncomePreviousYear !== undefined
        ? netOperatingIncomePreviousYear + netOtherIncomePreviousYear
        : undefined;

    // Format report date and period
    const startDate = parseDate(reportPeriod.start);
    const endDate = parseDate(reportPeriod.end);
    const reportDate = (endDate || DateTime.now()).toFormat('MMMM d, yyyy');
    const reportPeriodFormatted =
      startDate && endDate
        ? `${startDate.toFormat(`MMM d${startDate.year !== endDate.year ? ', yyyy' : ''}`)} - ${endDate.toFormat('MMM d, yyyy')}`
        : 'All Dates';
    const accrualBasis = (endDate || DateTime.now()).toLocaleString(DateTime.DATETIME_FULL);

    return {
      organization: OrganizationComponent.templatize({ organization, customOrgName: globalOptions?.customOrgName }),
      accrualBasis,
      reportDate,
      reportPeriod: reportPeriodFormatted,
      income,
      costOfGoodsSold,
      expenses,
      otherIncome,
      otherExpenses,
      grossProfit: {
        balance: grossProfitValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        balanceRaw: grossProfitValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        balanceFmt: formatCurrency(grossProfitValue),
        previousPeriodBalance: grossProfitPreviousPeriod
          ? grossProfitPreviousPeriod.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          : undefined,
        previousYearBalance: grossProfitPreviousYear
          ? grossProfitPreviousYear.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          : undefined,
      },
      netOperatingIncome: {
        balance: netOperatingIncomeValue.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
        balanceRaw: netOperatingIncomeValue.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
        balanceFmt: formatCurrency(netOperatingIncomeValue),
        previousPeriodBalance: netOperatingIncomePreviousPeriod
          ? netOperatingIncomePreviousPeriod.toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })
          : undefined,
        previousYearBalance: netOperatingIncomePreviousYear
          ? netOperatingIncomePreviousYear.toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })
          : undefined,
      },
      netOtherIncome: {
        balance: netOtherIncomeValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        balanceRaw: netOtherIncomeValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        balanceFmt: formatCurrency(netOtherIncomeValue),
        previousPeriodBalance: netOtherIncomePreviousPeriod
          ? netOtherIncomePreviousPeriod.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          : undefined,
        previousYearBalance: netOtherIncomePreviousYear
          ? netOtherIncomePreviousYear.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          : undefined,
      },
      netIncome: {
        balance: netIncomeValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        balanceRaw: netIncomeValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        balanceFmt: formatCurrency(netIncomeValue),
        previousPeriodBalance: netIncomePreviousPeriod
          ? netIncomePreviousPeriod.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          : undefined,
        previousYearBalance: netIncomePreviousYear
          ? netIncomePreviousYear.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          : undefined,
      },
    };
  },
  definitionMap: {
    organization: { label: 'Organization', type: 'object', children: OrganizationComponent.definitionMap },
    accrualBasis: { label: 'Accrual Basis', type: 'string' },
    reportDate: { label: 'Report Date', type: 'string' },
    reportPeriod: { label: 'Report Period', type: 'string' },
    income: {
      label: 'Income',
      type: 'object',
      children: {
        balance: { label: 'Balance', type: 'string' },
        balanceRaw: { label: 'Balance Raw', type: 'string' },
        balanceFmt: { label: 'Balance Formatted', type: 'string' },
        previousPeriodBalance: { label: 'Previous Period Balance', type: 'string' },
        previousYearBalance: { label: 'Previous Year Balance', type: 'string' },
        accounts: {
          label: 'Accounts',
          type: 'list',
          children: {
            id: { label: 'ID', type: 'string' },
            accountNumber: { label: 'Account Number', type: 'string' },
            accountName: { label: 'Account Name', type: 'string' },
            accountType: { label: 'Account Type', type: 'string' },
            balance: { label: 'Balance', type: 'string' },
            balanceRaw: { label: 'Balance Raw', type: 'string' },
            balanceNum: { label: 'Balance Number', type: 'number' },
            balanceFmt: { label: 'Balance Formatted', type: 'string' },
            previousPeriodBalance: { label: 'Previous Period Balance', type: 'string' },
            previousYearBalance: { label: 'Previous Year Balance', type: 'string' },
            depth: { label: 'Depth', type: 'number' },
            hasChildren: { label: 'Has Children', type: 'boolean' },
            isLastChild: { label: 'Is Last Child', type: 'boolean' },
            isSubtotal: { label: 'Is Subtotal', type: 'boolean' },
            isZeroBalance: { label: 'Is Zero Balance', type: 'boolean' },
            parentGroup: { label: 'Parent Group', type: 'string' },
          },
        },
      },
    },
    costOfGoodsSold: {
      label: 'Cost of Goods Sold',
      type: 'object',
      children: {
        balance: { label: 'Balance', type: 'string' },
        balanceRaw: { label: 'Balance Raw', type: 'string' },
        balanceFmt: { label: 'Balance Formatted', type: 'string' },
        previousPeriodBalance: { label: 'Previous Period Balance', type: 'string' },
        previousYearBalance: { label: 'Previous Year Balance', type: 'string' },
        accounts: {
          label: 'Accounts',
          type: 'list',
          children: {
            id: { label: 'ID', type: 'string' },
            accountNumber: { label: 'Account Number', type: 'string' },
            accountName: { label: 'Account Name', type: 'string' },
            accountType: { label: 'Account Type', type: 'string' },
            balance: { label: 'Balance', type: 'string' },
            balanceRaw: { label: 'Balance Raw', type: 'string' },
            balanceNum: { label: 'Balance Number', type: 'number' },
            balanceFmt: { label: 'Balance Formatted', type: 'string' },
            previousPeriodBalance: { label: 'Previous Period Balance', type: 'string' },
            previousYearBalance: { label: 'Previous Year Balance', type: 'string' },
            depth: { label: 'Depth', type: 'number' },
            hasChildren: { label: 'Has Children', type: 'boolean' },
            isLastChild: { label: 'Is Last Child', type: 'boolean' },
            isSubtotal: { label: 'Is Subtotal', type: 'boolean' },
            isZeroBalance: { label: 'Is Zero Balance', type: 'boolean' },
            parentGroup: { label: 'Parent Group', type: 'string' },
          },
        },
      },
    },
    expenses: {
      label: 'Expenses',
      type: 'object',
      children: {
        balance: { label: 'Balance', type: 'string' },
        balanceRaw: { label: 'Balance Raw', type: 'string' },
        balanceFmt: { label: 'Balance Formatted', type: 'string' },
        previousPeriodBalance: { label: 'Previous Period Balance', type: 'string' },
        previousYearBalance: { label: 'Previous Year Balance', type: 'string' },
        accounts: {
          label: 'Accounts',
          type: 'list',
          children: {
            id: { label: 'ID', type: 'string' },
            accountNumber: { label: 'Account Number', type: 'string' },
            accountName: { label: 'Account Name', type: 'string' },
            accountType: { label: 'Account Type', type: 'string' },
            balance: { label: 'Balance', type: 'string' },
            balanceRaw: { label: 'Balance Raw', type: 'string' },
            balanceNum: { label: 'Balance Number', type: 'number' },
            balanceFmt: { label: 'Balance Formatted', type: 'string' },
            previousPeriodBalance: { label: 'Previous Period Balance', type: 'string' },
            previousYearBalance: { label: 'Previous Year Balance', type: 'string' },
            depth: { label: 'Depth', type: 'number' },
            hasChildren: { label: 'Has Children', type: 'boolean' },
            isLastChild: { label: 'Is Last Child', type: 'boolean' },
            isSubtotal: { label: 'Is Subtotal', type: 'boolean' },
            isZeroBalance: { label: 'Is Zero Balance', type: 'boolean' },
            parentGroup: { label: 'Parent Group', type: 'string' },
          },
        },
      },
    },
    otherIncome: {
      label: 'Other Income',
      type: 'object',
      children: {
        balance: { label: 'Balance', type: 'string' },
        balanceRaw: { label: 'Balance Raw', type: 'string' },
        balanceFmt: { label: 'Balance Formatted', type: 'string' },
        previousPeriodBalance: { label: 'Previous Period Balance', type: 'string' },
        previousYearBalance: { label: 'Previous Year Balance', type: 'string' },
        accounts: {
          label: 'Accounts',
          type: 'list',
          children: {
            id: { label: 'ID', type: 'string' },
            accountNumber: { label: 'Account Number', type: 'string' },
            accountName: { label: 'Account Name', type: 'string' },
            accountType: { label: 'Account Type', type: 'string' },
            balance: { label: 'Balance', type: 'string' },
            balanceRaw: { label: 'Balance Raw', type: 'string' },
            balanceNum: { label: 'Balance Number', type: 'number' },
            balanceFmt: { label: 'Balance Formatted', type: 'string' },
            previousPeriodBalance: { label: 'Previous Period Balance', type: 'string' },
            previousYearBalance: { label: 'Previous Year Balance', type: 'string' },
            depth: { label: 'Depth', type: 'number' },
            hasChildren: { label: 'Has Children', type: 'boolean' },
            isLastChild: { label: 'Is Last Child', type: 'boolean' },
            isSubtotal: { label: 'Is Subtotal', type: 'boolean' },
            isZeroBalance: { label: 'Is Zero Balance', type: 'boolean' },
            parentGroup: { label: 'Parent Group', type: 'string' },
          },
        },
      },
    },
    otherExpenses: {
      label: 'Other Expenses',
      type: 'object',
      children: {
        balance: { label: 'Balance', type: 'string' },
        balanceRaw: { label: 'Balance Raw', type: 'string' },
        balanceFmt: { label: 'Balance Formatted', type: 'string' },
        previousPeriodBalance: { label: 'Previous Period Balance', type: 'string' },
        previousYearBalance: { label: 'Previous Year Balance', type: 'string' },
        accounts: {
          label: 'Accounts',
          type: 'list',
          children: {
            id: { label: 'ID', type: 'string' },
            accountNumber: { label: 'Account Number', type: 'string' },
            accountName: { label: 'Account Name', type: 'string' },
            accountType: { label: 'Account Type', type: 'string' },
            balance: { label: 'Balance', type: 'string' },
            balanceRaw: { label: 'Balance Raw', type: 'string' },
            balanceNum: { label: 'Balance Number', type: 'number' },
            balanceFmt: { label: 'Balance Formatted', type: 'string' },
            previousPeriodBalance: { label: 'Previous Period Balance', type: 'string' },
            previousYearBalance: { label: 'Previous Year Balance', type: 'string' },
            depth: { label: 'Depth', type: 'number' },
            hasChildren: { label: 'Has Children', type: 'boolean' },
            isLastChild: { label: 'Is Last Child', type: 'boolean' },
            isSubtotal: { label: 'Is Subtotal', type: 'boolean' },
            isZeroBalance: { label: 'Is Zero Balance', type: 'boolean' },
            parentGroup: { label: 'Parent Group', type: 'string' },
          },
        },
      },
    },
    grossProfit: {
      label: 'Gross Profit',
      type: 'object',
      children: {
        balance: { label: 'Balance', type: 'string' },
        balanceRaw: { label: 'Balance Raw', type: 'string' },
        balanceFmt: { label: 'Balance Formatted', type: 'string' },
        previousPeriodBalance: { label: 'Previous Period Balance', type: 'string' },
        previousYearBalance: { label: 'Previous Year Balance', type: 'string' },
      },
    },
    netOperatingIncome: {
      label: 'Net Operating Income',
      type: 'object',
      children: {
        balance: { label: 'Balance', type: 'string' },
        balanceRaw: { label: 'Balance Raw', type: 'string' },
        balanceFmt: { label: 'Balance Formatted', type: 'string' },
        previousPeriodBalance: { label: 'Previous Period Balance', type: 'string' },
        previousYearBalance: { label: 'Previous Year Balance', type: 'string' },
      },
    },
    netOtherIncome: {
      label: 'Net Other Income',
      type: 'object',
      children: {
        balance: { label: 'Balance', type: 'string' },
        balanceRaw: { label: 'Balance Raw', type: 'string' },
        balanceFmt: { label: 'Balance Formatted', type: 'string' },
        previousPeriodBalance: { label: 'Previous Period Balance', type: 'string' },
        previousYearBalance: { label: 'Previous Year Balance', type: 'string' },
      },
    },
    netIncome: {
      label: 'Net Income',
      type: 'object',
      children: {
        balance: { label: 'Balance', type: 'string' },
        balanceRaw: { label: 'Balance Raw', type: 'string' },
        balanceFmt: { label: 'Balance Formatted', type: 'string' },
        previousPeriodBalance: { label: 'Previous Period Balance', type: 'string' },
        previousYearBalance: { label: 'Previous Year Balance', type: 'string' },
      },
    },
  },
};
